import {
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import { Skeleton } from '@material-ui/lab'

import NoDataImage from '../../assets/images/No-Data.png'
import { getTranslate } from '../../localization'
import { InventoryComparisonRow } from '../../types/inventoryHealth'
import LayerValue from './LayerValue'
import { formatComparisonDate } from './comparisonDate'
import { findingLabel, findingText, statusColor } from './findings'
import useTableStyles from './styles/InventoryHealthTable.styles'
import useStyles from './styles/InventoryHealthTableMobile.styles'

type Props = {
  rows: InventoryComparisonRow[]
  loading: boolean
  onRefresh: () => void
  /** Loaded before column filtering, so an empty table can say WHY. */
  loadedCount: number
  onClearFilters: () => void
}

/**
 * Duplicates LAYOUT, never behaviour (§2 Responsive). The three-state reading
 * of a layer lives in LayerValue and the finding text in findings.ts, so the
 * two breakpoints cannot drift on the things that carry meaning.
 */
export default function InventoryHealthTableMobile({
  rows,
  loading,
  onRefresh,
  loadedCount,
  onClearFilters
}: Props) {
  const classes = useStyles()
  const isDark = useTheme().palette.type === 'dark'
  const tableClasses = useTableStyles()

  if (loading) {
    return (
      <>
        {Array(5)
          .fill('')
          .map((_, index) => (
            <Paper key={index} className={classes.card}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="90%" />
            </Paper>
          ))}
      </>
    )
  }

  if (rows.length === 0) {
    return (
      <Paper className={tableClasses.emptyState}>
        <img
          src={NoDataImage}
          alt=""
          className={tableClasses.emptyImage}
          aria-hidden="true"
        />
        {/*
          Filtered-empty and server-empty are different situations with
          different remedies, and collapsing them into one sentence is the
          false-reassurance failure this screen exists to remove — in a
          smaller place. Only one of them is recoverable from here.
        */}
        {loadedCount > 0 ? (
          <>
            <Typography variant="body2" color="textSecondary">
              {getTranslate(
                'No rows match the current column filters. {total} rows are loaded.',
                { total: loadedCount }
              )}
            </Typography>
            <Button onClick={onClearFilters}>
              {getTranslate('Clear filters')}
            </Button>
          </>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {getTranslate('No inventory was returned for this selection.')}
          </Typography>
        )}
      </Paper>
    )
  }

  return (
    <>
      {/*
        Desktop puts refresh in the header row, which mobile has no equivalent
        of. Placement differs; the CAPABILITY must not — losing the action here
        would be exactly the behavioural drift this file's docstring forbids.
      */}
      <div className={classes.refreshRow}>
        <IconButton
          size="small"
          onClick={onRefresh}
          aria-label={getTranslate('Refresh')}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </div>

      {rows.map((row) => (
        <Paper
          key={`${row.date}-${row.roomTypeCode}-${row.ratePlanCode}-${row.channelID}`}
          className={classes.card}>
          <div className={classes.heading}>
            <Typography variant="subtitle2">
              {formatComparisonDate(row.date)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {row.channelName || row.channelID}
            </Typography>
          </div>

          <Typography variant="body2">
            {(row.roomTypeName || row.roomTypeCode) +
              ' · ' +
              (row.ratePlanName || row.ratePlanCode)}
          </Typography>

          <div className={classes.layers}>
            {(['pms', 'crs', 'channel'] as const).map((layer) => (
              <div key={layer}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.layerLabel}>
                  {getTranslate(
                    layer === 'channel' ? 'Channel value' : layer.toUpperCase()
                  )}
                </Typography>
                <LayerValue value={row[layer]} />
              </div>
            ))}
          </div>

          <div
            className={classes.assessment}
            style={{ color: statusColor(row.status, isDark) }}>
            {row.findingCodes.length === 0
              ? getTranslate('Agrees')
              : row.findingCodes.map((code) => (
                  <Tooltip key={code} title={findingText(code)}>
                    <Typography
                      variant="body2"
                      tabIndex={0}
                      aria-label={findingText(code)}>
                      {findingLabel(code)}
                    </Typography>
                  </Tooltip>
                ))}
          </div>
        </Paper>
      ))}
    </>
  )
}
